// ---------------------------------------------------------------------------
// C5-C8: Design Copilot Sidebar — conversion score, heatmap, issues, fix all
// ---------------------------------------------------------------------------
import { useState, useMemo, useCallback } from "react";
import { apiFetch } from "@/lib/api-client";
import {
  applyOptimizationRecommendations,
  canAutoApplyOptimizationRecommendation,
  type OptimizationAnalysisResult,
  type OptimizationApplyResult,
  type OptimizationRecommendation,
  type OptimizationSeoInput,
} from "@ease/page-builder/services";
import type { PuckComponent } from "@ease/page-builder/ai";

function heatColor(attention: number): string {
  if (attention > 0.7) return "bg-[var(--color-destructive)]/30 border-[var(--color-destructive)]/40";
  if (attention > 0.4) return "bg-[var(--color-warning)]/20 border-[var(--color-warning)]/40";
  return "bg-[var(--color-primary)]/10 border-[var(--color-primary)]/30";
}

function scoreColor(total: number): string {
  if (total >= 70) return "text-[var(--color-success)]";
  if (total >= 40) return "text-[var(--color-warning)]";
  return "text-[var(--color-destructive)]";
}

interface DesignCopilotProps {
  pageId?: string;
  analysis: OptimizationAnalysisResult;
  components: PuckComponent[];
  seo?: OptimizationSeoInput;
  onApplyResult: (result: OptimizationApplyResult) => void;
  showHeatmap: boolean;
  onHeatmapChange: (showHeatmap: boolean) => void;
}

export default function DesignCopilot({
  pageId,
  analysis,
  components,
  seo,
  onApplyResult,
  showHeatmap,
  onHeatmapChange,
}: DesignCopilotProps) {
  const [isApplying, setIsApplying] = useState(false);
  const [statusMessage, setStatusMessage] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<"score" | "issues" | "heatmap">(showHeatmap ? "heatmap" : "score");

  const score = analysis.conversion;
  const heatmap = analysis.heatmap;
  const applyModeLabel = pageId ? "mounted editor draft" : "current editor draft";

  const issues = useMemo(
    () => analysis.recommendations.map((recommendation) => ({
      recommendation,
      factor: issueFactorLabel(recommendation),
      issue: recommendation.description,
      suggestion: recommendation.action ?? "",
      canApply: canAutoApplyOptimizationRecommendation(recommendation.id),
    })),
    [analysis.recommendations],
  );
  const autoFixableIssues = useMemo(
    () => issues.filter((issue) => issue.canApply),
    [issues],
  );

  const applyResult = useCallback((result: OptimizationApplyResult) => {
    setStatusMessage(buildApplyStatusMessage(result));
    onApplyResult(result);
  }, [onApplyResult]);

  const runApply = useCallback(async (recommendationIds?: string[]) => {
    setIsApplying(true);

    try {
      const result = pageId
        ? await apiFetch<{ data: OptimizationApplyResult }>(
          `/crm/pages/${pageId}/audits/analysis/apply`,
          {
            method: "POST",
            body: JSON.stringify({ components, seo, recommendationIds }),
          },
        ).then((response) => response.data)
        : applyOptimizationRecommendations({
          components,
          seo,
          recommendationIds,
        });

      applyResult(result);
    } catch {
      setStatusMessage("Unable to apply recommendations to the current draft.");
    } finally {
      setIsApplying(false);
    }
  }, [applyResult, components, pageId, seo]);

  const handleFixAll = useCallback(async () => {
    if (autoFixableIssues.length === 0) {
      setStatusMessage("No deterministic Fix All actions are available for this draft.");
      return;
    }
    await runApply(autoFixableIssues.map((issue) => issue.recommendation.id));
  }, [autoFixableIssues, runApply]);

  const handleApplyIssue = useCallback(async (recommendationId: string) => {
    await runApply([recommendationId]);
  }, [runApply]);

  const handleTabChange = useCallback((tab: "score" | "issues" | "heatmap") => {
    setActiveTab(tab);
    onHeatmapChange(tab === "heatmap");
  }, [onHeatmapChange]);

  return (
    <div className="w-72 shrink-0 border-l border-border bg-background overflow-y-auto">
      <div className="p-4 space-y-4">
        <div className="text-center">
          <div className={`text-4xl font-bold ${scoreColor(score.total)}`}>{score.total}</div>
          <div className="text-xs text-muted-foreground">Estimated conversion score</div>
          <div className="mt-1 text-xs text-muted-foreground">Overall {analysis.scores.overall}</div>
          {score.topIssue ? <div className="mt-1 text-xs text-[var(--color-warning)]">{score.topIssue}</div> : null}
        </div>

        <div className="rounded border border-border bg-muted/30 px-3 py-2 text-[11px] text-muted-foreground">
          Copilot analysis is predictive. Apply updates change the {applyModeLabel}, then the editor reflects those changes immediately.
        </div>

        <div className="flex border-b border-border">
          {(["score", "issues", "heatmap"] as const).map((tab) => (
            <button
              key={tab}
              onClick={() => handleTabChange(tab)}
              className={`flex-1 pb-2 text-xs font-medium ${activeTab === tab ? "border-b-2 border-primary text-foreground" : "text-muted-foreground"}`}
            >
              {tab === "score" ? "Factors" : tab === "issues" ? `Issues (${issues.length})` : "Heatmap"}
            </button>
          ))}
        </div>

        {statusMessage ? (
          <div className="rounded border border-border bg-muted/40 px-3 py-2 text-[11px] text-muted-foreground">
            {statusMessage}
          </div>
        ) : null}

        {activeTab === "score" ? (
          <div className="space-y-2">
            {score.factors.map((factor) => (
              <div key={factor.id} className="flex items-center gap-2">
                <div className="w-20 truncate text-xs text-muted-foreground">{factor.name}</div>
                <div className="flex-1 h-2 overflow-hidden rounded-full bg-muted">
                  <div
                    className={`h-full rounded-full ${factor.score > 0.7 ? "bg-[var(--color-success)]/50" : factor.score > 0.3 ? "bg-[var(--color-warning)]/50" : "bg-[var(--color-destructive)]/50"}`}
                    style={{ width: `${factor.score * 100}%` }}
                  />
                </div>
                <div className="w-6 text-right text-xs text-muted-foreground">{Math.round(factor.score * factor.weight)}</div>
              </div>
            ))}
          </div>
        ) : null}

        {activeTab === "issues" ? (
          <div className="space-y-2">
            {issues.length === 0 ? (
              <p className="py-4 text-center text-xs text-[var(--color-success)]">No issues detected!</p>
            ) : (
              <>
                {issues.map((issue) => (
                  <div key={issue.recommendation.id} className="rounded border border-border p-2 text-xs">
                    <div className="font-medium text-foreground">{issue.issue}</div>
                    <div className="mt-0.5 flex items-center justify-between gap-2 text-muted-foreground">
                      <span>{issue.factor}</span>
                      <span className="uppercase tracking-wide">{issue.recommendation.impact}</span>
                    </div>
                    {issue.suggestion ? <div className="mt-1 text-[10px] text-primary">{issue.suggestion}</div> : null}
                    <button
                      onClick={() => handleApplyIssue(issue.recommendation.id)}
                      disabled={isApplying || !issue.canApply}
                      className="mt-2 w-full rounded border border-border px-2 py-1.5 text-[11px] font-medium text-foreground disabled:cursor-not-allowed disabled:opacity-50"
                    >
                      {issue.canApply ? `Apply to ${applyModeLabel}` : "Manual fix required"}
                    </button>
                  </div>
                ))}
                <button
                  onClick={handleFixAll}
                  disabled={isApplying || autoFixableIssues.length === 0}
                  className="w-full rounded-md bg-primary px-3 py-2 text-xs font-medium text-primary-foreground disabled:opacity-50"
                >
                  {isApplying ? "Applying..." : `Fix All in ${applyModeLabel} (${autoFixableIssues.length}/${issues.length})`}
                </button>
              </>
            )}
          </div>
        ) : null}

        {activeTab === "heatmap" ? (
          <div className="space-y-2">
            <p className="text-xs text-muted-foreground">Predicted attention based on section order, CTA placement, and component type. This is guidance, not live user tracking.</p>
            {heatmap.map((item) => (
              <div key={`${item.componentType}-${item.componentIndex}`} className={`rounded border p-2 text-xs ${heatColor(item.attention)}`}>
                <div className="flex items-center justify-between gap-2">
                  <span>{item.componentType} #{item.componentIndex + 1}</span>
                  <span className="font-mono">{Math.round(item.attention * 100)}%</span>
                </div>
                {item.reasons[0] ? (
                  <div className="mt-1 text-[10px] text-muted-foreground">
                    {item.reasons[0]}
                  </div>
                ) : null}
              </div>
            ))}
            <div className="flex justify-between pt-2 text-[10px] text-muted-foreground">
              <span className="text-sky-500">Cold</span>
              <span className="text-amber-500">Warm</span>
              <span className="text-red-500">Hot</span>
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );
}

function issueFactorLabel(recommendation: OptimizationRecommendation): string {
  if (recommendation.category === "conversion") {
    return `Conversion · ${recommendation.title}`;
  }
  if (recommendation.category === "seo") {
    return `SEO · ${recommendation.title}`;
  }
  return `Performance · ${recommendation.title}`;
}

function buildApplyStatusMessage(result: OptimizationApplyResult): string {
  if (result.applied.length > 0 && result.skipped.length === 0) {
    return `Applied ${result.applied.length} recommendation${result.applied.length === 1 ? "" : "s"} to the current draft.`;
  }
  if (result.applied.length > 0 && result.skipped.length > 0) {
    return `Applied ${result.applied.length} to the current draft; skipped ${result.skipped.length}: ${result.skipped[0]?.reason ?? "manual review required"}`;
  }
  if (result.skipped.length > 0) {
    return `No fixes applied: ${result.skipped[0]?.reason ?? "manual review required"}`;
  }
  return "No changes were required.";
}
